import React from 'react';
import { Box, ButtonBase, Link } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { tokens } from '../../../theme/tokens';

/**
 * The few pieces every Início card is made of, so the personal and the group
 * pages read as one: a panel, its header, the small caps label, a big number
 * and a soft tile with a number in it.
 */

const merge = (base: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> =>
  [base, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;

export const Panel: React.FC<{ label: string; children: React.ReactNode; sx?: SxProps<Theme>; hero?: boolean }> = ({
  label,
  children,
  sx,
  hero,
}) => (
  <Box
    component='section'
    aria-label={label}
    sx={merge(
      {
        minWidth: 0,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: hero ? '22px' : `${tokens.radius.card}px`,
        p: hero ? { xs: 2.25, md: 3.25 } : { xs: 2.25, md: 2.75 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      },
      sx,
    )}
  >
    {children}
  </Box>
);

export const PanelHeader: React.FC<{
  title: string;
  action?: { label: string; onClick: () => void };
  aside?: React.ReactNode;
}> = ({ title, action, aside }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
    <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 17, fontWeight: 650, lineHeight: 1.3 }}>
      {title}
    </Box>
    {action && (
      <Link component='button' type='button' underline='none' onClick={action.onClick} sx={{ fontSize: 13, fontWeight: 600 }}>
        {action.label}
      </Link>
    )}
    {aside && <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{aside}</Box>}
  </Box>
);

export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component='span'
    sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink3 }}
  >
    {children}
  </Box>
);

export const BigNumber: React.FC<{ children: React.ReactNode; size?: number; color?: string }> = ({
  children,
  size = 46,
  color,
}) => (
  <Box
    component='span'
    sx={{
      fontFamily: tokens.display,
      fontSize: { xs: Math.round(size * 0.82), md: size },
      fontWeight: 700,
      letterSpacing: '-0.03em',
      lineHeight: 1,
      fontVariantNumeric: 'tabular-nums',
      color: color ?? tokens.ink,
    }}
  >
    {children}
  </Box>
);

export const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'plain' | 'accent' | 'warn';
  valueColor?: string;
  size?: number;
}> = ({ label, value, hint, tone = 'plain', valueColor, size = 24 }) => {
  const soft = tone === 'accent' ? tokens.ink2 : tone === 'warn' ? tokens.warn : tokens.ink3;
  return (
    <Box
      sx={{
        p: { xs: 1.25, md: 2 },
        borderRadius: '16px',
        bgcolor: tone === 'accent' ? tokens.accentSoft : tone === 'warn' ? tokens.warnSoft : tokens.bg,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
      }}
    >
      <Box component='span' sx={{ fontSize: 13, color: soft }}>{label}</Box>
      <Box
        component='span'
        sx={{
          fontFamily: tokens.display,
          // three tiles share a phone's width: small enough that "R$ 9.700" stays on one line
          fontSize: { xs: Math.min(16, Math.round(size * 0.8)), md: size },
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: valueColor ?? (tone === 'accent' ? tokens.accent : tone === 'warn' ? tokens.warn : tokens.ink),
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Box>
      {hint && <Box component='span' sx={{ fontSize: 12.5, color: soft }}>{hint}</Box>}
    </Box>
  );
};

/** A thin bar: the share of something, in one color. */
export const Meter: React.FC<{ ratio: number; color?: string; height?: number; track?: string; marker?: number }> = ({
  ratio,
  color = tokens.accent,
  height = 8,
  track = tokens.bg,
  marker,
}) => (
  <Box sx={{ position: 'relative', height, borderRadius: 99, bgcolor: track }}>
    <Box sx={{ width: `${Math.max(0, Math.min(ratio, 1)) * 100}%`, height, borderRadius: 99, bgcolor: color }} />
    {marker !== undefined && (
      <Box
        aria-hidden
        sx={{ position: 'absolute', left: `${Math.max(0, Math.min(marker, 1)) * 100}%`, top: -4, width: 2, height: height + 8, bgcolor: tokens.ink }}
      />
    )}
  </Box>
);

/** A row that opens something: a group, a list. Full width, at least 44px tall. */
export const RowButton: React.FC<{ onClick: () => void; children: React.ReactNode; sx?: SxProps<Theme> }> = ({ onClick, children, sx }) => (
  <ButtonBase
    onClick={onClick}
    sx={merge(
      {
        width: '100%',
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.25,
        px: 1.75,
        py: 1.5,
        borderRadius: '14px',
        border: `1px solid ${tokens.line}`,
        textAlign: 'left',
        font: 'inherit',
        color: tokens.ink,
        '&:hover': { bgcolor: tokens.bg },
        '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
      },
      sx,
    )}
  >
    {children}
  </ButtonBase>
);

/** A member's initial in their own color. */
export const PersonDot: React.FC<{ name: string; color: string; tint: string; size?: number; ring?: boolean }> = ({
  name,
  color,
  tint,
  size = 34,
  ring,
}) => (
  <Box
    component='span'
    aria-hidden
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: '50%',
      bgcolor: tint,
      color,
      border: ring ? `2px solid ${tokens.bg}` : 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: Math.round(size * 0.38),
    }}
  >
    {name.charAt(0).toUpperCase()}
  </Box>
);

/** The little pill next to a transaction: "pelo Fin", "pendente". */
export const Tag: React.FC<{ tone: 'accent' | 'warn'; children: React.ReactNode; icon?: React.ReactNode; sx?: SxProps<Theme> }> = ({
  tone,
  children,
  icon,
  sx,
}) => (
  <Box
    component='span'
    sx={merge(
      {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.1,
        py: 0.4,
        borderRadius: 99,
        whiteSpace: 'nowrap',
        fontSize: 12,
        fontWeight: 600,
        bgcolor: tone === 'accent' ? tokens.accentSoft : tokens.warnSoft,
        color: tone === 'accent' ? tokens.accent : tokens.warn,
      },
      sx,
    )}
  >
    {icon}
    {children}
  </Box>
);

/** Plain text for an empty card. */
export const Quiet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='p' sx={{ m: 0, py: 0.5, fontSize: 14, color: tokens.ink3 }}>{children}</Box>
);
